import * as Yup from 'yup'
import {useState, useEffect} from 'react'
import {useNavigate, Link} from 'react-router-dom'
import {useFormik, Form, FormikProvider} from 'formik'
import {useSnackbar} from 'notistack5'
import {Icon} from '@iconify/react'
import useNetworkMonitor from 'utils/hooks/useNetworkMonitor'
// material
import {
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Modal,
  Backdrop,
  Fade,
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
  CircularProgress,
} from '@material-ui/core'
import CloseIcon from '@mui/icons-material/Close'
import eyeFill from '@iconify/icons-eva/eye-fill'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import eyeOffFill from '@iconify/icons-eva/eye-off-fill'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import userLight from '@iconify/icons-ph/user-light'
import {LoadingButton} from '@material-ui/lab'
import LocalPhoneTwoToneIcon from '@mui/icons-material/LocalPhoneTwoTone'
import CredentialLoginIcon from 'assets/svg/loginSwitchedCred.png'
import storage from 'utils/storage'
import useAuth from 'utils/api/auth'
import Bugsnag from '@bugsnag/js'
import userApi from 'utils/api/users'
import QrReader from 'react-qr-reader'
import EmployeeVisualAid from 'assets/svg/stk-employee-reg-visual-aid.png'
import {ArrowBackIos, ArrowForwardIos} from '@mui/icons-material'
import {set} from 'lodash'
import Numpad from './Numpad'
import FourDigitDisplay from './FourDigitDisplay'
import AccountChangeMPIN from 'components/account/AccountChangeMPIN'
// ----------------------------------------------------------------------

export default function LoginForm({currentStep, handleEmailForm}) {
  const navigate = useNavigate()
  const {enqueueSnackbar} = useSnackbar()
  const [isLoading, setLoading] = useState(false)

  // Use shared network monitoring
  useNetworkMonitor()
  const [showPassword, setShowPassword] = useState(false)
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [openVerification, setOpenVerification] = useState(false)
  const [openPassword, setOpenPassword] = useState(false)
  const [openQR, setOpenQR] = useState(false)
  const [openCreateAccount, setOpenCreateAccount] = useState(false)
  const [scanMessage, setScanMessage] = useState('No data found')
  const [initialChoice, setInitialChoice] = useState(false) // Tracks initial choice modal visibility
  const [isStoreManager, setIsStoreManager] = useState(false) // Tracks if user chose "Store Manager"
  const [isProceed, setIsProceed] = useState(false) // Tracks if user chose to proceed to account creation
  const [submitMPINMessage, setSubmitMPINMessage] = useState('')
  const [openResetMPIN, setOpenResetMPIN] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false) // Prevent multiple submissions
  const [isArchived, setIsArchived] = useState(false)
  const LoginSchema = Yup.object().shape({
    phone: Yup.string()
      .min(11, 'Not a valid phone number! (ex. 091523468790)')
      .max(11, 'Not a valid phone number! (ex. 091523468790)'),
    mpin: isProceed
      ? Yup.string()
          .min(4, 'Not a valid MPIN! (ex. 1234)')
          .max(4, 'Not a valid MPIN! (ex. 1234)')
          .required('MPIN is required')
      : Yup.string(),
  })

  const formik = useFormik({
    initialValues: {
      phone: '',
      remember: storage.getRememberPhone() ? true : false,
      mpin: '',
    },
    validationSchema: LoginSchema,
    onSubmit: async (values) => {
      setLoading(true)

      try {
        if (!values.phone) {
          enqueueSnackbar('Please input mobile no.', {variant: 'warning'})
          setLoading(false)
          return
        }

        // Add timeout to the API call
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Login request timed out')), 30000),
        )
        const uiVersion = localStorage.getItem('uiVersion') || 'old'
        let result
        
        if (isArchived) {
          try {
            const loginResult = await useAuth.archived_user_login(values.phone, values.mpin)
            if (loginResult && loginResult.data && loginResult.data.success) {
              // Store token and user data for archived access
              await storage.storeToken(loginResult.data.token)
              await storage.storeUser(loginResult.data.user)
  
              // Redirect to archived gate page with authentication
              navigate('/archived', {
                state: {
                  userId: loginResult.data.user._id,
                  isArchived: true,
                  role: loginResult.data.user.role || 0,
                  company: loginResult.data.user.company || 'Unknown Company'
                }
              }, {replace: true})
              return
            }else{
              enqueueSnackbar(loginResult.data.msg, {variant: 'error'})
              setSubmitMPINMessage(loginResult.data.msg)
              setFieldValue('mpin', '')
              setLoading(false)
              return
            }
          } catch (loginError) {
            console.error('Archived user login error:', loginError)
          }
        } else {
          result = await Promise.race([useAuth.sign_in_phone(values.phone, values.mpin, uiVersion), timeoutPromise])
        }

        if (!result.ok) {
          setSubmitMPINMessage(result.data.msg)
          setFieldValue('mpin', '')
          setLoading(false)
          return
        }

        let {data} = result
        if (data.role === 0) {
          data.sid = data.store_id
        }

        // Check if user account is archived
        if (data.isArchived) {
          setLoading(false)
          navigate(`/archived`, {replace: true})
          return
        }

        if (!JSON.parse(data.isOnBoarded)) {
          setLoading(false)
          navigate(`/${data.sid}/onboard`)
          return
        }

        resetForm()

        // Store user data and token regardless of remember me
        await storage.storeUser(data)
        await storage.storeToken(data.token)

        // Store or clear credentials based on remember me selection
        if (values.remember) {
          // If "Remember me" is checked, store phone number
          await storage.storeRememberPhone({phone: values.phone})
        } else {
          // If "Remember me" is not checked, clear remembered phone
          await storage.removeRememberPhone()
        }

        setLoading(false)

        if (data.role === 1 || data.role >= 3) {
          navigate(`/stores/app`, {replace: true})
          return
        }

        navigate(`/dashboard/app`, {replace: true})
      } catch (error) {
        console.error('Phone login error:', error)
        setLoading(false)

        if (error.message === 'Login request timed out') {
          enqueueSnackbar('Login request timed out. Please check your internet connection and try again.', {
            variant: 'error',
          })
        } else {
          enqueueSnackbar('An error occurred during login. Please try again.', {variant: 'error'})
        }
      }

      /*      let { data } = result
      data.sid = data.store_id
      await storage.storeUser(data)
      await storage.storeToken(data.token)
      setLoading(false)
      if (data.verificationCode === null) {
        if (data.isVerified && data.isOnBoarded) {
          if (data.role === 1 || data.role === 4) return navigate('/stores/app')
          navigate(`/dashboard/app`)
        } else if (data.isVerified && !data.isOnBoarded) {
          navigate(`/${data.store_id}/onboard`)
        } else if (!data.isVerified && !data.isOnBoarded) {
          navigate(`/${data.store_id}/onboard`)
        }
      } else {
        currentStep(2)
      }*/
    },
  })

  const {errors, touched, values, handleSubmit, getFieldProps, resetForm, setFieldValue} = formik

  // Load remembered phone number on component mount
  useEffect(() => {
    const loadRememberedPhone = async () => {
      try {
        const rememberedPhone = storage.getRememberPhone()
        if (rememberedPhone && rememberedPhone.phone) {
          formik.setFieldValue('phone', rememberedPhone.phone)
          formik.setFieldValue('remember', true)
        }
      } catch (error) {
        console.log('Error loading remembered phone number', error)
      }
    }

    loadRememberedPhone()
  }, [setFieldValue])

  const handleShowPassword = () => {
    setShowPassword((show) => !show)
  }

  const handleCloseModal = () => {
    setOpen(false)
  }

  const handleOpenVerificationModal = async () => {
    setLoading(true)
    const data = {
      email: email,
      type: 'forgot_password',
    }
    const result = await userApi.get_verification_code(data)
    if (result.data.success) {
      enqueueSnackbar('Please check verification code sent to the email address', {variant: 'success'})
      setOpenVerification(true)
      setOpen(false)
    } else {
      enqueueSnackbar(result.data.msg, {variant: 'error'})
    }
    setLoading(false)
  }

  const handleCloseVerificationModal = () => {
    setOpenVerification(false)
    setOpen(false)
  }

  const handleSubmitVerification = async () => {
    setLoading(true)
    const data = {
      email: email,
      token: code,
    }
    const result = await userApi.set_verification_code(data)
    if (result.data.success) {
      setOpenPassword(true)
      setOpenVerification(false)
      setOpen(false)
    } else {
      enqueueSnackbar(result.data.msg, {variant: 'error'})
      setOpenPassword(false)
    }
    setLoading(false)
  }

  const handleCloseFeedback = () => {
    setSubmitMPINMessage('')
  }

  const handleResetMPIN = async () => {
    setSubmitMPINMessage('')
    setOpenResetMPIN(true)
  }

  const handleCloseResetMPIN = () => {
    setSubmitMPINMessage('')
    setOpenResetMPIN(false)
  }

  const handleNewPassword = async () => {
    setLoading(true)
    if (!password) {
      enqueueSnackbar('Please input new password', {variant: 'warning'})
    } else {
      const data = {
        email: email,
        password: password,
      }
      const result = await userApi.set_new_password(data)
      if (result.status === 200) {
        enqueueSnackbar('Password successfully updated', {variant: 'success'})
        setOpenPassword(false)
        setOpenVerification(false)
        setOpen(false)
      } else {
        enqueueSnackbar('Something went wrong please contact administrator', {variant: 'error'})
      }
    }
    setLoading(false)
  }

  const handleClosePassword = () => {
    setOpenPassword(false)
    setOpenVerification(false)
    setOpen(false)
  }

  const handleScan = async (res) => {
    if (res) {
      window.location.href = res
    } else {
      enqueueSnackbar('No link found in the response.', {variant: 'warning'})
    }
  }

  const handleCloseQR = async () => {
    setOpenQR(false)
  }

  const handleError = (err) => {
    enqueueSnackbar(err, {variant: 'error'})
  }

  const handleOpenCreateAccount = async () => {
    setInitialChoice(true)
    // setOpenCreateAccount(true)
  }

  const handleUserChoice = (choice) => {
    if (choice === 'Employee') {
      setIsStoreManager(false)
      setOpenCreateAccount(true)
    } else if (choice === 'Client Manager') {
      setIsStoreManager(true)
      setOpenCreateAccount(true)
    }
    setInitialChoice(false)
  }

  const handleCloseCreateAccount = async () => {
    setOpenCreateAccount(false)
  }

  const handleProceed = async () => {
    if (isSubmitting) return // Prevent multiple submissions

    setIsSubmitting(true)
    setLoading(true)
    try {
      if (!values.phone) {
        enqueueSnackbar('Please input mobile no.', {variant: 'warning'})
        return
      }

      // Add timeout to the API call
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), 30000))

      const result = await Promise.race([useAuth.check_phone(formik.values.phone), timeoutPromise])

      if (result.data.success) {
        setIsProceed(true)
      } else {
        if (result.data.msg === 'MPIN not yet set') {
          enqueueSnackbar('MPIN not yet set, please set it in your profile', {variant: 'warning'})
          // Don't proceed to MPIN input, let user set MPIN in profile first
          setLoading(false)
          handleSubmit();
          return
        }

        if (result.data.isArchived) {
          setIsArchived(true)
          let failCount = 0
          if (values.phone) {
            try {
              const loginResult = await useAuth.archived_user_login(values.phone, values.mpin)
              if (loginResult && loginResult.data && loginResult.data.success) {
                // Store token and user data for archived access
                await storage.storeToken(loginResult.data.token)
                await storage.storeUser(loginResult.data.user)

                // Redirect to archived gate page with authentication
                navigate('/archived', {
                  state: {
                    userId: loginResult.data.user._id,
                    isArchived: true,
                    role: loginResult.data.user.role || 0,
                    company: loginResult.data.user.company || 'Unknown Company'
                  }
                }, { replace: true })
                return
              } else {
                failCount++
                if(loginResult.data.msg === 'Invalid MPIN' && failCount === 1){
                  setFieldValue('mpin', '')
                  setLoading(false)
                  setIsProceed(true)
                  return
                }
                enqueueSnackbar(loginResult.data.msg, {variant: 'error'})
                setSubmitMPINMessage(loginResult.data.msg)
                setFieldValue('mpin', '')
                setLoading(false)
                return
              }
            } catch (loginError) {
              console.error('Archived user login error:', loginError)
            }
          }
        }

        if (result.data.msg === 'Invalid phone number') {
          enqueueSnackbar('Mobile no. not found', {variant: 'error'})
          setLoading(false)
          return
        }

        enqueueSnackbar('Something went wrong, please try again.', {variant: 'error'})
        setLoading(false)
        return
      }
    } catch (err) {
      console.log(err)
      if (err.message === 'Request timed out') {
        enqueueSnackbar('Request timed out. Please check your internet connection and try again.', {variant: 'error'})
      } else {
        enqueueSnackbar('Something went wrong, please try again.', {variant: 'error'})
      }
    } finally {
      setLoading(false)
      setIsSubmitting(false)
    }
  }

  const handleBackMPIN = () => {
    setFieldValue('mpin', '')
    setIsProceed(false)
  }

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleProceed(); }} noValidate method="post">
        {isProceed ? (
          <Stack spacing={3}>
            <Box sx={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <Typography variant="h5" gutterBottom>
                Enter MPIN
              </Typography>
            </Box>
            <FourDigitDisplay value={formik.values.mpin} type={'enter'} />
            <Box sx={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <Numpad
                onChange={(value) => setFieldValue('mpin', value)}
                onEnter={() => handleSubmit()}
                onClear={() => setFieldValue('mpin', '')}
                initialValue={formik.values.mpin}
              />
            </Box>

            <Box display={'flex'} justifyContent={'space-between'} gap={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleBackMPIN}
                startIcon={<ArrowBackIos />}
                sx={{borderRadius: 28, flexGrow: 1}}
              >
                Back
              </Button>
              {/* <LoadingButton
              fullWidth
              variant="contained"
              color="secondary"
              type="submit" 
              endIcon={<ArrowForwardIos/>}
              sx={{ borderRadius: 28, flexGrow: 1 }}
            >
              Enter
            </LoadingButton> */}
            </Box>
          </Stack>
        ) : (
          <Box sx={{paddingLeft: '1.5rem', paddingRight: '1.5rem'}}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                type="tel"
                inputProps={{maxLength: 11, minLength: 11}}
                label="Mobile number"
                autoFocus
                {...getFieldProps('phone')}
                error={Boolean(touched.phone && errors.phone)}
                helperText={touched.phone && errors.phone}
                InputProps={{
                  sx: {
                    borderRadius: 28,
                    backgroundColor: '#F6F6F6',
                    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.30)', // Adjust the shadow values
                  },
                  startAdornment: (
                    <InputAdornment position="center">
                      <IconButton position="center">
                        <LocalPhoneTwoToneIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  // endAdornment: (
                  //   <InputAdornment position="end">
                  //       <div
                  //         style={{
                  //           width: '40px',
                  //           height: '40px',
                  //           border: '3px solid #1F98E8',
                  //           borderRadius: '50%',
                  //           overflow: 'hidden',
                  //         }}
                  //         onClick={() => handleSwitchForm(false)}
                  //       >
                  //         <img
                  //           width="100%"
                  //           height="100%"
                  //           style={{ filter: 'invert(60%) sepia(100%) saturate(300%) hue-rotate(180deg)' }}
                  //           src={CredentialLoginIcon}
                  //           alt="custom-icon"
                  //         />
                  //       </div>
                  //   </InputAdornment>
                  // )
                }}
              />
              {/*          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            {...getFieldProps('password')}
            InputProps={{
              sx: {
                borderRadius: 28,
                backgroundColor: '#F6F6F6',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.30)', // Adjust the shadow values
              },
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton position='start'>
                    <LockOutlinedIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />*/}
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{my: 2}}>
              <FormControlLabel
                control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
                label="Remember me"
                sx={{fontSize: '.5rem', color: '#1F98E8'}}
              />

              <Link onClick={() => setOpen(true)} variant="subtitle2" to="#" style={{color: '#1F98E8'}}>
                Forgot password?
              </Link>
            </Stack>

            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isLoading}
              disabled={isLoading || isSubmitting}
              loadingIndicator={<CircularProgress color="primary" />}
              style={{
                borderRadius: 28,
                color: 'white',
                backgroundColor: '#1F98E8',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.40)',
              }}
            >
              Login
            </LoadingButton>

            <Typography
              onClick={() => handleEmailForm()}
              variant="body1"
              color="#1F98E8"
              sx={{textAlign: 'center', mt: '1rem'}}
            >
              Login using credentials
            </Typography>
            <Typography
              variant="body1"
              onClick={handleOpenCreateAccount}
              color="#1F98E8"
              sx={{
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                mt: 1,
              }}
            >
              {/*<QrCodeScannerIcon />*/}
              Create an account
            </Typography>
          </Box>
        )}
      </Form>
      <Dialog open={open} onClose={handleCloseModal}>
        <DialogTitle>
          Forgot Password?
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>Enter registered email address you used to sign-up.</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Email Address"
            type="email"
            fullWidth
            value={email}
            variant="standard"
            onChange={(e) => setEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          {isLoading ? (
            <Button onClick={handleOpenVerificationModal} disabled>
              Submit
            </Button>
          ) : (
            <Button onClick={handleOpenVerificationModal}>Submit</Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={openVerification} onClose={handleCloseVerificationModal}>
        <DialogTitle>
          Verification Code
          <IconButton
            aria-label="close"
            onClick={handleCloseVerificationModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>Enter verification code sent through your email.</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Verification Code"
            type="email"
            fullWidth
            variant="standard"
            onChange={(e) => setCode(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVerificationModal}>Cancel</Button>
          {isLoading ? (
            <Button onClick={handleSubmitVerification} disabled>
              Submit
            </Button>
          ) : (
            <Button onClick={handleSubmitVerification}>Submit</Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={openPassword} onClose={handleClosePassword}>
        <DialogTitle>
          New Password
          <IconButton
            aria-label="close"
            onClick={handleClosePassword}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePassword}>Cancel</Button>
          {isLoading ? (
            <Button onClick={handleNewPassword} disabled>
              Submit
            </Button>
          ) : (
            <Button onClick={handleNewPassword}>Submit</Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={openQR} onClose={handleCloseQR}>
        <DialogTitle>
          Scan store QR code.
          <IconButton
            aria-label="close"
            onClick={handleCloseQR}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <>
            <QrReader
              delay={3000}
              onError={handleError}
              onScan={handleScan}
              // chooseDeviceId={()=>selected}
              style={{width: '250px', heigth: '200px'}}
              // className={'qrScanner'}
            />
            <DialogTitle style={{display: 'flex', justifyContent: 'center'}}>
              <a href="https://www.sparkletimekeeping.com/store/create">Register store account</a>
            </DialogTitle>
          </>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openCreateAccount}
        onClose={handleCloseCreateAccount}
        maxWidth="xs" // Adjusting width to make it similar to the image
        fullWidth
      >
        <DialogTitle sx={{textAlign: 'center', pb: 0}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <img src="/favicon/logo.jpg" alt="Sparkle Timekeeping" style={{width: 100}} />
            <Typography variant="h6" fontWeight="bold" sx={{ml: 2}}>
              SPARKLE TIMEKEEPING
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{textAlign: 'center', px: 5, py: 3}}>
          <Box sx={{backgroundColor: '#f8f9fa', borderRadius: 2, p: 3, boxShadow: 1}}>
            {isStoreManager ? (
              <>
                <Typography variant="h6" fontWeight="bold" sx={{mb: 2}}>
                  Create Client Account
                </Typography>
                <Typography variant="body2" sx={{mb: 3}}>
                  Proceed to create a new client account.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => (window.location.href = '/store/create')}
                >
                  Create Client Account
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight="bold" sx={{mb: 2}}>
                  Create Employee Account
                </Typography>
                <Typography variant="body2" sx={{mb: 3}}>
                  Scan client QR code to proceed
                </Typography>
                <Box sx={{mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <img src={EmployeeVisualAid} alt="Visual Aid" style={{width: 150, marginBottom: '1rem'}} />
                  <br />
                  <br />
                  <img
                    src="https://media.istockphoto.com/id/1358621997/vector/qr-code-smartphone-scanner-linear-icon-vector-illustration.jpg?s=612x612&w=0&k=20&c=ePiWZHIbseW9GwmM498rRKC_Dvk8IsKv41nqnC8iZhQ="
                    alt="QR code"
                    style={{
                      width: 150,
                      marginBottom: '1rem',
                      borderRadius: 2,
                      boxShadow: 1,
                      border: '1px solid #f8f9fa',
                      padding: 2,
                      backgroundColor: '#fff',
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => setOpenQR(true)}
                  sx={{
                    textTransform: 'none',
                    px: 5,
                    animation: 'pulse 1s infinite', // Add pulse animation
                    '@keyframes pulse': {
                      '0%': {transform: 'scale(1)'},
                      '50%': {transform: 'scale(1.05)'},
                      '100%': {transform: 'scale(1)'},
                    },
                  }}
                >
                  Scan Now
                </Button>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{justifyContent: 'center', flexDirection: 'column', py: 2}}>
          {/* <Typography variant="body2" sx={{ mb: 2 }}>
  Already have an account?{' '}
  <Button color="primary" sx={{ textTransform: 'none', p: 0 }} 
  onClick={handleCloseCreateAccount}>
    Sign in here
  </Button>
</Typography> */}

          <Button onClick={handleCloseCreateAccount} variant="outlined" color="error" sx={{mt: 2}}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={initialChoice} onClose={() => setInitialChoice(false)}>
        <DialogTitle>Choose User Type</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you an employee or a Client?</Typography>
          <Stack direction="row" spacing={2} sx={{mt: 2}}>
            <Button variant="contained" onClick={() => handleUserChoice('Employee')}>
              Employee
            </Button>
            <Button variant="outlined" onClick={() => handleUserChoice('Client Manager')}>
              Client Manager
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog open={!!submitMPINMessage} onClose={handleCloseFeedback}>
        <DialogTitle sx={{textAlign: 'center'}}>{submitMPINMessage}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{textAlign: 'center', mb: 3}}>
            Would you like to reset your MPIN or try again?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            '& .MuiButton-root': {
              width: '100%',
              py: 1.5,
              mb: 1,
              fontSize: '1rem',
            },
          }}
        >
          <Stack direction="column" spacing={2} sx={{width: '100%'}}>
            <Button onClick={handleCloseFeedback} color="primary" variant="contained" sx={{flex: 1}}>
              Try again
            </Button>
            <Button onClick={() => handleResetMPIN()} color="secondary" variant="outlined" sx={{flex: 1}}>
              Reset MPIN
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <AccountChangeMPIN open={openResetMPIN} onClose={handleCloseResetMPIN} phone={values.phone} />
    </FormikProvider>
  )
}
